import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const { login } = useAuth();
  const { prefetch } = useDataCache();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Get redirect destination from location state
  const from = location.state?.from?.pathname || null;

  useEffect(() => {
    if (!lockoutUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setLockoutSeconds(0);
        clearInterval(interval);
      } else {
        setLockoutSeconds(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockoutUntil && Date.now() < lockoutUntil) return;
    setLoading(true);

    // Trim the email: iOS/Android keyboards often append a trailing space on
    // autocomplete, which made valid credentials look "not recognized".
    const result = await login({ email: email.trim(), password });

    if (result.success) {
      setFailedAttempts(0);

      // Prefetch dashboard data in the background so it's ready when user navigates
      const r = result.user.role;
      if (r === 'dg' || r === 'admin') {
        prefetch([
          { key: 'dg-stats', url: '/api/dg/dashboard/stats' },
          { key: 'dg-sectors', url: '/api/dg/dashboard/sectors' },
          { key: 'dg-leaders', url: '/api/dg/dashboard/leader-activity' },
          { key: 'dg-alerts', url: '/api/dg/dashboard/alerts' },
        ]);
      } else if (r === 'leader') {
        prefetch([
          { key: 'leader-dashboard', url: '/api/leader/dashboard/stats' },
        ]);
      } else if (r === 'member') {
        prefetch([
          { key: 'member-dashboard', url: '/api/member/dashboard' },
          { key: 'member-events', url: '/api/events-pcc/' },
        ]);
      }

      toast({
        title: 'Connexion réussie',
        description: `Bienvenue ${result.user.displayName || result.user.firstName}!`,
      });
      
      // Determine appropriate redirect destination based on role
      let redirectTo;

      // Settings/profile paths must NEVER be used as a post-login landing —
      // a user who's just logged in should always see the role's HOME first,
      // not a settings screen. This fixes the recurring "leader lands on
      // Profil instead of dashboard" bug that happens when `from` captures
      // a settings URL (e.g. after a session expiry on the settings page).
      const isSettingsOrProfilePath = (p) =>
        !!p && (
          p.endsWith('/settings') ||
          p.endsWith('/profile') ||
          p.includes('/settings/') ||
          p.includes('/profile/')
        );

      // Validate 'from' path against user's role
      if (from && !isSettingsOrProfilePath(from)) {
        const userRole = result.user.role;
        const isLeaderPath = from.startsWith('/leader') || from.startsWith('/utilisateur');
        const isMemberPath = from.startsWith('/member');

        // Only redirect to 'from' if user has permission
        if (isLeaderPath && (userRole === 'leader' || userRole === 'admin' || userRole === 'staff')) {
          redirectTo = from;
        } else if (isMemberPath && (userRole === 'member' || userRole === 'leader' || userRole === 'admin' || userRole === 'staff')) {
          redirectTo = from;
        } else if (!isLeaderPath && !isMemberPath) {
          // Non-protected path, allow redirect
          redirectTo = from;
        }
      }

      // Default redirect based on role if no valid 'from' path
      if (!redirectTo) {
        if (result.user.role === 'leader' && result.user.isReconciler === true) {
          redirectTo = '/leader/reconciler';  // réconciliateur dédié (Coralie) → son tableau de bord d'accueil
        } else if (result.user.role === 'leader' || result.user.role === 'admin' || result.user.role === 'staff') {
          redirectTo = '/leader/dashboard';
        } else if (result.user.role === 'dg') {
          redirectTo = '/dg/dashboard';  // la Direction atterrit sur sa Vue globale, pas l'accueil public
        } else if (result.user.role === 'visitor') {
          redirectTo = '/';  // Visitors go to the public home
        } else {
          redirectTo = '/member/home';
        }
      }
      
      navigate(redirectTo);
    } else {
      const newCount = failedAttempts + 1;
      setFailedAttempts(newCount);
      if (newCount >= 5) {
        const until = Date.now() + 30000;
        setLockoutUntil(until);
        setLockoutSeconds(30);
      }
      toast({
        title: 'Erreur de connexion',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-200 ${'bg-pcc-bg'}`}>
      <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
        <CardHeader className="space-y-1">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            className={`absolute top-[max(env(safe-area-inset-top),12px)] left-4 p-2 transition-colors ${'text-pcc-text-muted hover:text-pcc-text'}`}
            data-testid="login-back-btn"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-4">
            <img src="/logo-lion-light.png" alt="Paris Centre Chrétien" className="w-20 h-20 mb-2 hidden dark:block" />
            <img src="/logo-lion-dark.png" alt="Paris Centre Chrétien" className="w-20 h-20 mb-2 dark:hidden" />
            <span className={`text-sm font-medium tracking-wide ${'text-pcc-text-muted'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Paris Centre Chrétien
            </span>
          </div>
          <CardTitle className={`text-2xl font-bold text-center ${'text-pcc-text'}`}>
            Connexion
          </CardTitle>
          <CardDescription className={`text-center ${'text-pcc-text-muted'}`}>
            Accédez à votre espace membre
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className={'text-pcc-text'}>Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`h-12 rounded-xl ${'bg-pcc-surface2 border-pcc-card-border text-pcc-text'}`}
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className={'text-pcc-text'}>Mot de passe</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-pcc-primary hover:underline"
                >
                  Mot de passe oublié?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`h-12 rounded-xl pr-12 ${'bg-pcc-surface2 border-pcc-card-border text-pcc-text'}`}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${'text-pcc-text-muted hover:text-pcc-text'}`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl font-semibold"
              disabled={loading || lockoutSeconds > 0}
              data-testid="login-submit"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
            {lockoutSeconds > 0 && (
              <p className="text-red-500 text-sm text-center mt-2">
                Trop de tentatives. Réessayez dans {lockoutSeconds}s
              </p>
            )}
          </form>
          
          <div className="mt-6 text-center text-sm">
            <span className={'text-pcc-text-muted'}>Pas encore de compte? </span>
            <Link to="/register" className="text-pcc-primary hover:underline font-semibold">
              S&apos;inscrire
            </Link>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
